import React, { useState, useEffect } from "react";
import { Row, Col, Button, Modal, Form, Badge } from "react-bootstrap";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import ResponsiveTable from "./ResponsiveTable";
import bonusService from "../services/bonusService";
import { toast } from "react-toastify";
import Spinner from "./Spinner";

const BonusTypes = () => {
  // State
  const [bonusTypes, setBonusTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    amount: "",
    isActive: true
  });

  // Load bonus types on component mount
  useEffect(() => {
    fetchBonusTypes();
  }, []);

  // Fetch bonus types
  const fetchBonusTypes = async () => {
    try {
      setLoading(true);
      const data = await bonusService.getBonusTypes();
      setBonusTypes(data);
    } catch (error) {
      toast.error("Failed to load bonus types");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the create modal
  const handleCreateModal = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      amount: "",
      isActive: true
    });
    setEditMode(false);
    setShowModal(true);
  };

  // Handle opening the edit modal for a bonus type
  const handleEditModal = (bonusType) => {
    setFormData({
      id: bonusType.id,
      name: bonusType.name,
      description: bonusType.description || "",
      amount: bonusType.amount.toString(),
      isActive: bonusType.isActive
    });
    setEditMode(true);
    setShowModal(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  // Handle saving a bonus type (create or update)
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Prepare data for API
      const bonusTypeData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editMode) {
        const updatedBonusType = await bonusService.updateBonusType(bonusTypeData.id, bonusTypeData);
        // Update local state directly
        setBonusTypes(prevTypes => 
          prevTypes.map(type => 
            type.id === updatedBonusType.id ? updatedBonusType : type
          )
        );
        toast.success("Bonus type updated successfully");
      } else {
        const newBonusType = await bonusService.createBonusType(bonusTypeData);
        // Add new bonus type to local state
        setBonusTypes(prevTypes => [...prevTypes, newBonusType]);
        toast.success("Bonus type created successfully");
      }
      
      setShowModal(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 
        `Failed to ${editMode ? "update" : "create"} bonus type`
      );
    }
  };

  // Handle deleting a bonus type
  const handleDelete = async (bonusTypeId) => {
    if (window.confirm("Are you sure you want to delete this bonus type?")) {
      try {
        await bonusService.deleteBonusType(bonusTypeId);
        // Remove from local state
        setBonusTypes(prevTypes => prevTypes.filter(type => type.id !== bonusTypeId));
        toast.success("Bonus type deleted successfully");
      } catch (error) {
        console.log("Error response:", error.response);
        const errorMessage = error.response?.data?.message || error.response?.data || "Failed to delete bonus type";
        toast.error(errorMessage);
      }
    }
  };

  // Handle toggling active status
  const handleToggleActive = async (bonusType) => {
    try {
      const updatedBonusType = {
        ...bonusType,
        isActive: !bonusType.isActive
      };
      const response = await bonusService.updateBonusType(bonusType.id, updatedBonusType);
      // Update local state directly
      setBonusTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === bonusType.id ? response : type
        )
      );
      toast.success(`Bonus type ${updatedBonusType.isActive ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update bonus type status"
      );
    }
  };

  // Table columns configuration
  const columns = [
    { key: "name", label: "Name", width: "15%" },
    { key: "description", label: "Description", width: "35%" },
    { key: "amount", label: "Default Amount", width: "20%", render: (amount) => `$${amount.toFixed(2)}` },
    { 
      key: "isActive", 
      label: "Status", 
      width: "15%",
      render: (isActive) => (
        <Badge bg={isActive ? "success" : "danger"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      ) 
    }
  ];

  // Table actions configuration
  const actions = [
    {
      label: "Edit",
      icon: <FaEdit />,
      variant: "outline-primary",
      onClick: handleEditModal
    },
    {
      label: "Toggle Status",
      icon: null,
      variant: "outline-warning",
      onClick: handleToggleActive,
      render: (bonusType) => (
        <Button 
          variant={bonusType.isActive ? "outline-warning" : "outline-success"}
          size="sm"
          onClick={() => handleToggleActive(bonusType)}
        >
          {bonusType.isActive ? "Deactivate" : "Activate"}
        </Button>
      )
    },
    {
      label: "Delete",
      icon: <FaTrash />,
      variant: "outline-danger",
      onClick: (bonusType) => handleDelete(bonusType.id)
    }
  ];

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
    
        <Button variant="primary" onClick={handleCreateModal}>
          <FaPlus className="me-1" /> Add Bonus Type
        </Button>
      </div>

      {loading ? (
        <Spinner />
      ) : bonusTypes.length === 0 ? (
        <p>No bonus types found. Create one to get started.</p>
      ) : (
        <ResponsiveTable
          data={bonusTypes}
          columns={columns}
          actions={actions}
        />
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit" : "Create"} Bonus Type</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter bonus type name"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description for this bonus type"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Default Amount</Form.Label>
              <Form.Control
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="Default bonus amount"
                step="0.01"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Only active bonus types can be selected for new bonus requests
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editMode ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BonusTypes; 